import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  Grid,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { ActivityLevel, ActivityLevelLabel } from "../../../../data/Types";
import StandardButton from "../../../../components/StandardButton";
import moment from "moment";
import { LanguageCodes } from "../../../../data/Types";
import { DaysOfWeek, DaysOfWeekString } from "../../../../data/GlobalData";

// Helper component for consistent field layout
const FormField = ({
  label,
  required = false,
  children,
  fullWidth = false,
}) => (
  <Box sx={{ marginBottom: 3 }}>
    <Typography
      variant="body2"
      sx={{
        marginBottom: 1,
        fontWeight: 500,
        color: "text.primary",
      }}
    >
      {label}
      {required && (
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "error.main", marginLeft: 0.5 }}
        >
          *
        </Typography>
      )}
    </Typography>
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>{children}</Box>
  </Box>
);

// Basic Information Tab
const BasicInfoTab = ({
  firstName,
  setFirstName,
  fieldErrors,
  lastName,
  setLastName,
  email,
  setEmail,
  language,
  setLanguage,
}) => (
  <Box sx={{ padding: 3 }}>
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormField label="First Name" required fullWidth>
          <TextField
            value={firstName}
            error={fieldErrors.firstName}
            fullWidth
            variant="outlined"
            onChange={(event) => setFirstName(event.target.value)}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Last Name" required fullWidth>
          <TextField
            value={lastName}
            error={fieldErrors.lastName}
            fullWidth
            variant="outlined"
            onChange={(event) => setLastName(event.target.value)}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Contact email (coach)" fullWidth>
          <TextField
            value={email}
            error={fieldErrors.email}
            fullWidth
            variant="outlined"
            onChange={(event) => setEmail(event.target.value)}
            helperText="For your records. The client’s Mighty45 sign-in email is shown on the client summary after they register."
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Language" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
          >
            {LanguageCodes.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormField>
      </Grid>
    </Grid>
  </Box>
);

// Physical Stats Tab
const PhysicalStatsTab = ({
  height,
  setHeight,
  weight,
  setWeight,
  startingWeight,
  setStartingWeight,
  birthDate,
  setBirthDate,
  sex,
  setSex,
}) => (
  <Box sx={{ padding: 3 }}>
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormField label="Height" fullWidth>
          <TextField
            value={height}
            fullWidth
            variant="outlined"
            onChange={(event) => setHeight(event.target.value)}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Goal Weight" fullWidth>
          <TextField
            value={weight}
            fullWidth
            variant="outlined"
            onChange={(event) => {
              let value = parseFloat(event.target.value);
              if (isNaN(value)) value = 0;
              setWeight(value);
            }}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Starting Weight" fullWidth>
          <TextField
            value={startingWeight || 0}
            fullWidth
            variant="outlined"
            onChange={(event) => {
              let value = parseFloat(event.target.value);
              if (isNaN(value)) value = 0;
              setStartingWeight(value);
            }}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Birth Date" fullWidth>
          <TextField
            type="date"
            value={birthDate ? moment(birthDate).format("YYYY-MM-DD") : ""}
            fullWidth
            variant="outlined"
            onChange={(event) => {
              const value = event.target.value;
              const birthDateValue = moment(value);
              setBirthDate(birthDateValue.toDate());
            }}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Gender" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={sex}
            onChange={(event) => setSex(event.target.value)}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </Select>
        </FormField>
      </Grid>
    </Grid>
  </Box>
);

// Fitness Info Tab
const FitnessInfoTab = ({
  goal,
  setGoal,
  experience,
  setExperience,
  activityLevel,
  setActivityLevel,
  checkInDay,
  setCheckInDay,
}) => (
  <Box sx={{ padding: 3 }}>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormField label="Fitness Goals" fullWidth>
          <TextField
            value={goal}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            onChange={(event) => setGoal(event.target.value)}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Experience Level" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={experience}
            onChange={(event) => setExperience(event.target.value)}
          >
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="expert">Expert</MenuItem>
          </Select>
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Activity Level" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={activityLevel}
            onChange={(event) => setActivityLevel(event.target.value)}
          >
            <MenuItem value={ActivityLevel.sedentary}>
              {ActivityLevelLabel.sedentary}
            </MenuItem>
            <MenuItem value={ActivityLevel.moderate1}>
              {ActivityLevelLabel.moderate1}
            </MenuItem>
            <MenuItem value={ActivityLevel.moderate2}>
              {ActivityLevelLabel.moderate2}
            </MenuItem>
            <MenuItem value={ActivityLevel.intense1}>
              {ActivityLevelLabel.intense1}
            </MenuItem>
            <MenuItem value={ActivityLevel.intense2}>
              {ActivityLevelLabel.intense2}
            </MenuItem>
            <MenuItem value={ActivityLevel.intense3}>
              {ActivityLevelLabel.intense3}
            </MenuItem>
          </Select>
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Check In Day" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={checkInDay}
            onChange={(event) => setCheckInDay(event.target.value)}
          >
            <MenuItem value={DaysOfWeek.monday}>
              {DaysOfWeekString[DaysOfWeek.monday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.tuesday}>
              {DaysOfWeekString[DaysOfWeek.tuesday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.wednesday}>
              {DaysOfWeekString[DaysOfWeek.wednesday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.thursday}>
              {DaysOfWeekString[DaysOfWeek.thursday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.friday}>
              {DaysOfWeekString[DaysOfWeek.friday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.saturday}>
              {DaysOfWeekString[DaysOfWeek.saturday]}
            </MenuItem>
            <MenuItem value={DaysOfWeek.sunday}>
              {DaysOfWeekString[DaysOfWeek.sunday]}
            </MenuItem>
          </Select>
        </FormField>
      </Grid>
    </Grid>
  </Box>
);

// Account Management Tab
const AccountManagementTab = ({
  status,
  setStatus,
  tier,
  setTier,
  renewDate,
  setRenewDate,
  untrackedMeals,
  setUntrackedMeals,
  protocolAge,
  setProtocolAge,
}) => (
  <Box sx={{ padding: 3 }}>
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormField label="Account Status" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="disabled">Inactive</MenuItem>
          </Select>
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Tier" fullWidth>
          <Select
            fullWidth
            variant="outlined"
            value={tier}
            onChange={(event) => setTier(event.target.value)}
          >
            <MenuItem value="tier1">Tier 1</MenuItem>
            <MenuItem value="tier2">Tier 2</MenuItem>
            <MenuItem value="tier3">Tier 3</MenuItem>
          </Select>
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Renew Date" fullWidth>
          <TextField
            type="date"
            value={renewDate ? moment(renewDate).format("YYYY-MM-DD") : ""}
            fullWidth
            variant="outlined"
            onChange={(event) => {
              const value = event.target.value;
              const renewDateValue = moment(value);
              setRenewDate(renewDateValue.toDate());
            }}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Untracked Meals" fullWidth>
          <TextField
            value={untrackedMeals}
            fullWidth
            variant="outlined"
            onChange={(event) => {
              let value = parseFloat(event.target.value);
              if (isNaN(value)) value = 0;
              setUntrackedMeals(value);
            }}
          />
        </FormField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormField label="Protocol Age (days)" fullWidth>
          <TextField
            value={protocolAge}
            fullWidth
            variant="outlined"
            type="number"
            helperText="Days before protocol is considered outdated"
            onChange={(event) => {
              let value = parseInt(event.target.value);
              if (isNaN(value) || value < 1) value = 90;
              setProtocolAge(value);
            }}
          />
        </FormField>
      </Grid>
    </Grid>
  </Box>
);

const ClientEditDialog = ({ client, onDialogClosed }) => {
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const [fieldErrors, setFieldErrors] = React.useState({
    firstName: false,
    lastName: false,
    email: false,
  });

  const [firstName, setFirstName] = React.useState(
    client ? client.firstName : "",
  );
  const [lastName, setLastName] = React.useState(client ? client.lastName : "");
  const [email, setEmail] = React.useState(
    client
      ? client.coachContactEmail || client.email || ""
      : "",
  );
  const [checkInDay, setCheckInDay] = React.useState(
    client && client.checkInDay ? client.checkInDay : "saturday",
  );

  const [height, setHeight] = React.useState(
    client ? client.statistics.height : "",
  );
  const [goal, setGoal] = React.useState(client ? client.statistics.goal : "");
  const [weight, setWeight] = React.useState(
    client ? client.statistics.weight : 0,
  );

  const [untrackedMeals, setUntrackedMeals] = React.useState(
    client && client.statistics.untrackedMeals
      ? client.statistics.untrackedMeals
      : 0,
  );

  const [startingWeight, setStartingWeight] = React.useState(
    client && client.statistics && client.statistics.startingWeight
      ? client.statistics.startingWeight
      : 0,
  );

  const [sex, setSex] = React.useState(
    client ? client.statistics.sex : "female",
  );

  const [birthDate, setBirthDate] = React.useState(
    client && client.statistics.birthDate
      ? client.statistics.birthDate.toDate()
      : null,
  );

  const [renewDate, setRenewDate] = React.useState(
    client && client.renewDate ? client.renewDate.toDate() : null,
  );

  const [language, setLanguage] = React.useState(
    client && client.language ? client.language : "en",
  );

  const [experience, setExperience] = React.useState(
    client ? client.statistics.experience : "beginner",
  );
  const [activityLevel, setActivityLevel] = React.useState(
    client && client.statistics.activityLevel
      ? client.statistics.activityLevel
      : ActivityLevel.sedentary,
  );

  const [status, setStatus] = React.useState(
    client && client.status ? client.status : "active",
  );
  const [tier, setTier] = React.useState(
    client && client.tier ? client.tier : "tier1",
  );
  const [protocolAge, setProtocolAge] = React.useState(
    client && client.protocolAge ? client.protocolAge : 90,
  );

  function validate() {
    if (firstName.length === 0) {
      setFieldErrors({
        firstName: true,
        lastName: false,
        email: false,
      });
      return "First name is required.";
    }
    if (lastName.length === 0) {
      setFieldErrors({
        firstName: false,
        lastName: true,
        email: false,
      });
      return "Last name is required.";
    }
    // if (email.length === 0) {
    //   setFieldErrors({
    //     firstName: false,
    //     lastName: false,
    //     email: true,
    //   });
    //   return "Email is required.";
    // }
    if (email.length !== 0) {
      // validate the email
      const re = /\S+@\S+\.\S+/;
      if (!re.test(email)) {
        setFieldErrors({
          firstName: false,
          lastName: false,
          email: true,
        });
        return "Invalid email address.";
      }
    }
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={true}
      onClose={() => {
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          {client ? "Edit Client" : "Add Client"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ backgroundColor: "background.paper" }}
          >
            <Tab label="Basic Info" />
            <Tab label="Physical Stats" />
            <Tab label="Fitness Info" />
            <Tab label="Account" />
          </Tabs>
        </Box>

        <Box sx={{ minHeight: 400 }}>
          {activeTab === 0 && (
            <BasicInfoTab
              firstName={firstName}
              setFirstName={setFirstName}
              fieldErrors={fieldErrors}
              lastName={lastName}
              setLastName={setLastName}
              email={email}
              setEmail={setEmail}
              language={language}
              setLanguage={setLanguage}
            />
          )}
          {activeTab === 1 && (
            <PhysicalStatsTab
              height={height}
              setHeight={setHeight}
              weight={weight}
              setWeight={setWeight}
              startingWeight={startingWeight}
              setStartingWeight={setStartingWeight}
              birthDate={birthDate}
              setBirthDate={setBirthDate}
              sex={sex}
              setSex={setSex}
            />
          )}
          {activeTab === 2 && (
            <FitnessInfoTab
              goal={goal}
              setGoal={setGoal}
              experience={experience}
              setExperience={setExperience}
              activityLevel={activityLevel}
              setActivityLevel={setActivityLevel}
              checkInDay={checkInDay}
              setCheckInDay={setCheckInDay}
            />
          )}
          {activeTab === 3 && (
            <AccountManagementTab
              status={status}
              setStatus={setStatus}
              tier={tier}
              setTier={setTier}
              renewDate={renewDate}
              setRenewDate={setRenewDate}
              untrackedMeals={untrackedMeals}
              setUntrackedMeals={setUntrackedMeals}
              protocolAge={protocolAge}
              setProtocolAge={setProtocolAge}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {error && (
          <Box style={{ display: "flex", alignItems: "center" }}>
            <Typography style={{ width: 200, color: "red" }}>
              {error}
            </Typography>
          </Box>
        )}

        <Button
          onClick={() => {
            if (onDialogClosed) {
              onDialogClosed(null);
            }
          }}
          color="primary"
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            const validation = validate();
            if (!validation) {
              if (onDialogClosed) {
                const data = {
                  firstName: firstName,
                  lastName: lastName,
                  email: email,
                  height: height,
                  sex: sex,
                  experience: experience,
                  goal: goal,
                  weight: weight,
                  startingWeight: startingWeight,
                  status: status,
                  activityLevel: activityLevel
                    ? activityLevel
                    : ActivityLevel.sedentary,
                  tier: tier,
                  birthDate: birthDate,
                  language: language,
                  renewDate: renewDate,
                  checkInDay: checkInDay,
                  untrackedMeals: untrackedMeals,
                  protocolAge: protocolAge,
                };

                onDialogClosed(data);
              }
            } else {
              setError(validation);
            }
          }}
        >
          Save
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default ClientEditDialog;
